/**
 * React Query mutation hooks for manual readings.
 * Handles create, update, and delete operations.
 */

import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";
import {
  onlineManager,
  QueryClient,
  QueryKey,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useNotification } from "app/NotificationProvider";
import { guid } from "common/guid";
import { APP_MUTATION_SCOPE } from "common/mutationScope";
import {
  invalidateUnlessReplaying,
  isOfflineReplayActive,
} from "common/offlineReplayCoordinator";
import { queryKeys } from "common/queryKeys";
import {
  createNetworkMutationRetryDelay,
  shouldRetryNetworkMutation,
} from "common/utils/networkMutationRetry";
import { syncAnimalGroupNowFromMoveEvents } from "model/fieldCacheHelpers";
import {
  OfflineMutationBlockedError,
  useGuardedMutation,
} from "model/useGuardedMutation";
import { clearTilesFromCache } from "services/fieldOperations";
import {
  EventType,
  ManualDMInputResponse,
  manualInputApi,
  manualInputMutationApi,
  prepareReadingForApi,
  processReadingResponse,
  ReadingMutationResult,
} from "./readingsApiHelpers";
import type { MoveEvent } from "./useAnimalGroupQueries";

const OFFLINE_MESSAGE = "You need to be online to use this.";
const NEW_READING_GUID = "new";
const generatedCreateGuids = new WeakMap<ManualDMInputResponse, string>();

interface ReadingMutationContext {
  snapshot: Array<[QueryKey, unknown]>;
}

function isGrazingReading(reading: ManualDMInputResponse): boolean {
  const eventType = reading.manualDryMatterInput.event?.eventType;
  return (
    eventType === EventType.GrazingEvent ||
    eventType === EventType.PartialGrazingEvent
  );
}

function animalGroupGuidFromReading(
  reading: ManualDMInputResponse
): string | undefined {
  return reading.manualDryMatterInput.event?.animalGroupGuid || undefined;
}

function createGuidForReading(reading: ManualDMInputResponse): string {
  if (reading.guid && reading.guid !== NEW_READING_GUID) return reading.guid;

  const existingGuid = generatedCreateGuids.get(reading);
  if (existingGuid) return existingGuid;

  const generatedGuid = guid();
  generatedCreateGuids.set(reading, generatedGuid);
  return generatedGuid;
}

function withCreateGuid(reading: ManualDMInputResponse): ManualDMInputResponse {
  const guid = createGuidForReading(reading);
  return guid === reading.guid ? reading : { ...reading, guid };
}

function grazingAffectedFieldGuids(
  queryClient: QueryClient,
  params: { fieldId: string; animalGroupGuid?: string }
): string[] {
  const fieldGuids = new Set<string>([params.fieldId]);
  if (!params.animalGroupGuid) return [...fieldGuids];

  const moveEvents = queryClient.getQueryData<
    Array<{
      fieldGuid: string | null;
      dateTimeOut: string | null;
    }>
  >(queryKeys.animalGroups.moveEvents(params.animalGroupGuid));

  for (const ev of moveEvents ?? []) {
    if (ev.dateTimeOut === null && ev.fieldGuid) {
      fieldGuids.add(ev.fieldGuid);
    }
  }

  return [...fieldGuids];
}

function resetTilesForGrazingChange(
  queryClient: QueryClient,
  params: { fieldId: string; animalGroupGuid?: string }
): void {
  for (const fieldGuid of grazingAffectedFieldGuids(queryClient, params)) {
    clearTilesFromCache(fieldGuid, queryClient);
    void queryClient.invalidateQueries({
      queryKey: queryKeys.fields.detail(fieldGuid),
    });
  }
}

function resetTilesForReadingMutationResult(
  queryClient: QueryClient,
  result: ReadingMutationResult
): void {
  const fieldGuids = (result.affectedFields ?? [])
    .filter((field) => field.tilesInvalidated)
    .map((field) => field.fieldGuid);
  for (const fieldGuid of fieldGuids) {
    clearTilesFromCache(fieldGuid, queryClient);
    void queryClient.invalidateQueries({
      queryKey: queryKeys.fields.detail(fieldGuid),
    });
  }
}

function snapshotReadings(
  queryClient: QueryClient,
  fieldId: string
): Array<[QueryKey, unknown]> {
  const key = queryKeys.events.readings.byField(fieldId);
  const current = queryClient.getQueryData<ManualDMInputResponse[]>(key) ?? [];
  return [[key, current]];
}

function restoreSnapshot(
  queryClient: QueryClient,
  snapshot: Array<[QueryKey, unknown]>
): void {
  snapshot.forEach(([key, value]) => {
    queryClient.setQueryData(key, value);
  });
}

function updateReadingList(
  queryClient: QueryClient,
  fieldId: string,
  updater: (old: ManualDMInputResponse[]) => ManualDMInputResponse[]
): void {
  queryClient.setQueryData<ManualDMInputResponse[]>(
    queryKeys.events.readings.byField(fieldId),
    (old) => updater(old ?? [])
  );
}

function moveEventsKey(animalGroupGuid: string): QueryKey {
  return queryKeys.animalGroups.moveEvents(animalGroupGuid);
}

function isMoveEventsQueryKey(queryKey: QueryKey): boolean {
  return (
    queryKey[0] === "animalGroups" &&
    queryKey[1] === "moveEvents" &&
    typeof queryKey[2] === "string"
  );
}

function moveEventQueryKeysContainingReading(
  queryClient: QueryClient,
  readingGuid: string
): QueryKey[] {
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: ["animalGroups", "moveEvents"] })
    .filter((query) => {
      if (!isMoveEventsQueryKey(query.queryKey)) return false;
      const data = query.state.data;
      return (
        Array.isArray(data) &&
        data.some((event) => (event as MoveEvent).guid === readingGuid)
      );
    })
    .map((query) => query.queryKey);
}

function snapshotMoveEvents(
  queryClient: QueryClient,
  animalGroupGuid?: string
): Array<[QueryKey, unknown]> {
  if (!animalGroupGuid) return [];
  const key = moveEventsKey(animalGroupGuid);
  const current = queryClient.getQueryData<MoveEvent[]>(key) ?? [];
  return [[key, current]];
}

function snapshotMoveEventsForReading(
  queryClient: QueryClient,
  reading: ManualDMInputResponse
): Array<[QueryKey, unknown]> {
  const animalGroupGuid = reading.manualDryMatterInput.event?.animalGroupGuid;
  const keys = animalGroupGuid
    ? [moveEventsKey(animalGroupGuid)]
    : moveEventQueryKeysContainingReading(queryClient, reading.guid);
  return keys.map((key) => [
    key,
    queryClient.getQueryData<MoveEvent[]>(key) ?? [],
  ]);
}

function buildMoveEventFromReading(
  queryClient: QueryClient,
  reading: ManualDMInputResponse,
  existingMoveEvents: MoveEvent[],
  options: { moveGuid?: string; existingGuid?: string } = {}
): MoveEvent {
  const fieldId = reading.manualDryMatterInput.field;
  const moveGuid = options.moveGuid ?? reading.guid;
  const existingGuid = options.existingGuid ?? moveGuid;
  const fieldName =
    queryClient.getQueryData<{ name?: string }>(
      queryKeys.fields.detail(fieldId)
    )?.name ?? null;
  const existing =
    existingMoveEvents.find((event) => event.guid === existingGuid) ??
    existingMoveEvents.find((event) => event.guid === moveGuid);
  const moveNumber =
    existing?.moveNumber ??
    existingMoveEvents.reduce(
      (max, event) => Math.max(max, event.moveNumber),
      0
    ) + 1;

  return {
    guid: moveGuid,
    moveNumber,
    fieldGuid: fieldId,
    fieldName,
    dateTimeIn: reading.manualDryMatterInput.event?.dateTimeIn ?? null,
    dateTimeOut: reading.manualDryMatterInput.event?.dateTimeOut ?? null,
    startDryMatter: reading.manualDryMatterInput.event?.preEventCover ?? null,
    endDryMatter: reading.manualDryMatterInput.event?.postEventCover ?? null,
    movedToShed: false,
    kind: reading.manualDryMatterInput.kind,
    verificationStatus: reading.manualDryMatterInput.verificationStatus,
    animalGroupGuid:
      reading.manualDryMatterInput.event?.animalGroupGuid ?? null,
  };
}

function applyMoveEventCacheForReading(
  queryClient: QueryClient,
  reading: ManualDMInputResponse,
  options: { moveGuid?: string; previousGuid?: string } = {}
): Array<[QueryKey, unknown]> {
  const animalGroupGuid = reading.manualDryMatterInput.event?.animalGroupGuid;
  if (!isGrazingReading(reading)) return [];

  const moveGuid = options.moveGuid ?? reading.guid;
  const previousGuid = options.previousGuid ?? moveGuid;
  const keys = animalGroupGuid
    ? [moveEventsKey(animalGroupGuid)]
    : moveEventQueryKeysContainingReading(queryClient, previousGuid);

  return keys.flatMap((key) => {
    const targetAnimalGroupGuid =
      typeof key[2] === "string" ? key[2] : animalGroupGuid;
    if (!targetAnimalGroupGuid) return [];

    const previousMoveEvents = queryClient.getQueryData<MoveEvent[]>(key) ?? [];
    const nextMoveEvent = buildMoveEventFromReading(
      queryClient,
      reading,
      previousMoveEvents,
      {
        moveGuid,
        existingGuid: previousGuid,
      }
    );
    const updatedMoveEvents = previousMoveEvents.some(
      (event) =>
        event.guid === previousGuid || event.guid === nextMoveEvent.guid
    )
      ? previousMoveEvents.map((event) =>
          event.guid === previousGuid || event.guid === nextMoveEvent.guid
            ? nextMoveEvent
            : event
        )
      : [...previousMoveEvents, nextMoveEvent];
    queryClient.setQueryData<MoveEvent[]>(key, updatedMoveEvents);
    return syncAnimalGroupNowFromMoveEvents(
      queryClient,
      targetAnimalGroupGuid,
      updatedMoveEvents,
      [reading.manualDryMatterInput.field]
    );
  });
}

function removeMoveEventFromCache(
  queryClient: QueryClient,
  params: { readingId: string; fieldId: string; animalGroupGuid?: string }
): Array<[QueryKey, unknown]> {
  if (!params.animalGroupGuid) return [];
  const key = moveEventsKey(params.animalGroupGuid);
  const previousMoveEvents = queryClient.getQueryData<MoveEvent[]>(key) ?? [];
  const updatedMoveEvents = previousMoveEvents.filter(
    (event) => event.guid !== params.readingId
  );
  queryClient.setQueryData<MoveEvent[]>(key, updatedMoveEvents);
  return syncAnimalGroupNowFromMoveEvents(
    queryClient,
    params.animalGroupGuid,
    updatedMoveEvents,
    [params.fieldId]
  );
}

function useQueuedReadingMutation<TData, TVariables>(
  options: UseMutationOptions<
    TData,
    unknown,
    TVariables,
    ReadingMutationContext
  >,
  canQueueOffline: (variables: TVariables) => boolean
): UseMutationResult<TData, unknown, TVariables, ReadingMutationContext> {
  const mutation = useMutation(options);
  const { notify } = useNotification();

  const mutate = ((
    variables: TVariables,
    mutateOptions?: Parameters<typeof mutation.mutate>[1]
  ) => {
    if (!onlineManager.isOnline() && !canQueueOffline(variables)) {
      notify(OFFLINE_MESSAGE, "error");
      return;
    }
    return mutation.mutate(variables, mutateOptions);
  }) as typeof mutation.mutate;

  const mutateAsync = ((
    variables: TVariables,
    mutateOptions?: Parameters<typeof mutation.mutateAsync>[1]
  ) => {
    if (!onlineManager.isOnline() && !canQueueOffline(variables)) {
      notify(OFFLINE_MESSAGE, "error");
      return Promise.reject(new OfflineMutationBlockedError());
    }
    return mutation.mutateAsync(variables, mutateOptions);
  }) as typeof mutation.mutateAsync;

  return { ...mutation, mutate, mutateAsync };
}

function createReadingMutationOptions(
  queryClient: QueryClient
): UseMutationOptions<
  ManualDMInputResponse,
  unknown,
  ManualDMInputResponse,
  ReadingMutationContext
> {
  return {
    networkMode: "online",
    scope: APP_MUTATION_SCOPE,
    retry: shouldRetryNetworkMutation,
    retryDelay: createNetworkMutationRetryDelay(),
    mutationFn: async (manualInput: ManualDMInputResponse) => {
      const inputWithCreateGuid = withCreateGuid(manualInput);
      const preparedInput = prepareReadingForApi(inputWithCreateGuid);
      const mutationResult =
        await manualInputMutationApi.createOneWithIdempotency(preparedInput);
      if (isOfflineReplayActive()) {
        return inputWithCreateGuid;
      }
      const guid = mutationResult.guid ?? inputWithCreateGuid.guid;
      const created = await manualInputApi.getOne(guid);
      return processReadingResponse(created);
    },
    onMutate: async (manualInput) => {
      const inputWithCreateGuid = withCreateGuid(manualInput);
      const fieldId = inputWithCreateGuid.manualDryMatterInput.field;
      await queryClient.cancelQueries({
        queryKey: queryKeys.events.readings.byField(fieldId),
      });
      const snapshot = snapshotReadings(queryClient, fieldId);
      updateReadingList(queryClient, fieldId, (old) => {
        const exists = old.some(
          (reading) => reading.guid === inputWithCreateGuid.guid
        );
        return exists
          ? old.map((reading) =>
              reading.guid === inputWithCreateGuid.guid
                ? inputWithCreateGuid
                : reading
            )
          : [...old, inputWithCreateGuid];
      });
      if (isGrazingReading(inputWithCreateGuid)) {
        const animalGroupGuid = animalGroupGuidFromReading(inputWithCreateGuid);
        snapshot.push(
          ...snapshotMoveEventsForReading(queryClient, inputWithCreateGuid)
        );
        snapshot.push(
          ...applyMoveEventCacheForReading(queryClient, inputWithCreateGuid)
        );
        resetTilesForGrazingChange(queryClient, {
          fieldId,
          animalGroupGuid,
        });
      }
      return { snapshot };
    },
    onError: (_error, manualInput, context) => {
      const inputWithCreateGuid = withCreateGuid(manualInput);
      if (context?.snapshot) {
        restoreSnapshot(queryClient, context.snapshot);
      }
      if (isGrazingReading(inputWithCreateGuid)) {
        resetTilesForGrazingChange(queryClient, {
          fieldId: inputWithCreateGuid.manualDryMatterInput.field,
          animalGroupGuid: animalGroupGuidFromReading(inputWithCreateGuid),
        });
      }
    },
    onSuccess: (data, manualInput) => {
      const inputWithCreateGuid = withCreateGuid(manualInput);
      const fieldId = inputWithCreateGuid.manualDryMatterInput.field;
      updateReadingList(queryClient, fieldId, (old) =>
        old.map((reading) =>
          reading.guid === inputWithCreateGuid.guid ? data : reading
        )
      );
      if (isGrazingReading(data)) {
        applyMoveEventCacheForReading(queryClient, data, {
          moveGuid: data.guid,
          previousGuid: inputWithCreateGuid.guid,
        });
      }
    },
    onSettled: (_data, _error, manualInput) => {
      const inputWithCreateGuid = withCreateGuid(manualInput);
      const fieldId = inputWithCreateGuid.manualDryMatterInput.field;
      void invalidateUnlessReplaying(queryClient, {
        queryKey: queryKeys.events.readings.byField(fieldId),
      });
      if (isGrazingReading(inputWithCreateGuid)) {
        for (const fieldGuid of grazingAffectedFieldGuids(queryClient, {
          fieldId,
          animalGroupGuid: animalGroupGuidFromReading(inputWithCreateGuid),
        })) {
          void invalidateUnlessReplaying(queryClient, {
            queryKey: queryKeys.fields.detail(fieldGuid),
          });
        }
      }
    },
  };
}

function updateReadingMutationOptions(
  queryClient: QueryClient
): UseMutationOptions<
  ManualDMInputResponse,
  unknown,
  ManualDMInputResponse,
  ReadingMutationContext
> {
  return {
    networkMode: "online",
    scope: APP_MUTATION_SCOPE,
    retry: shouldRetryNetworkMutation,
    retryDelay: createNetworkMutationRetryDelay(),
    mutationFn: async (manualInput: ManualDMInputResponse) => {
      const preparedInput = prepareReadingForApi(manualInput);
      await manualInputMutationApi.updateOne(preparedInput);
      if (isOfflineReplayActive()) {
        return manualInput;
      }
      const updated = await manualInputApi.getOne(manualInput.guid);
      return processReadingResponse(updated);
    },
    onMutate: async (manualInput) => {
      const fieldId = manualInput.manualDryMatterInput.field;
      await queryClient.cancelQueries({
        queryKey: queryKeys.events.readings.byField(fieldId),
      });
      const snapshot = snapshotReadings(queryClient, fieldId);
      updateReadingList(queryClient, fieldId, (old) =>
        old.map((reading) =>
          reading.guid === manualInput.guid ? manualInput : reading
        )
      );
      if (isGrazingReading(manualInput)) {
        const animalGroupGuid = animalGroupGuidFromReading(manualInput);
        snapshot.push(
          ...snapshotMoveEventsForReading(queryClient, manualInput)
        );
        snapshot.push(
          ...applyMoveEventCacheForReading(queryClient, manualInput)
        );
        resetTilesForGrazingChange(queryClient, {
          fieldId,
          animalGroupGuid,
        });
      }
      return { snapshot };
    },
    onError: (_error, manualInput, context) => {
      if (context?.snapshot) {
        restoreSnapshot(queryClient, context.snapshot);
      }
      if (isGrazingReading(manualInput)) {
        resetTilesForGrazingChange(queryClient, {
          fieldId: manualInput.manualDryMatterInput.field,
          animalGroupGuid: animalGroupGuidFromReading(manualInput),
        });
      }
    },
    onSuccess: (data, manualInput) => {
      const fieldId = manualInput.manualDryMatterInput.field;
      updateReadingList(queryClient, fieldId, (old) =>
        old.map((reading) => (reading.guid === data.guid ? data : reading))
      );
      if (isGrazingReading(data)) {
        applyMoveEventCacheForReading(queryClient, data, {
          moveGuid: data.guid,
          previousGuid: manualInput.guid,
        });
      }
    },
    onSettled: (_data, _error, manualInput) => {
      const fieldId = manualInput.manualDryMatterInput.field;
      void invalidateUnlessReplaying(queryClient, {
        queryKey: queryKeys.events.readings.byField(fieldId),
      });
      if (isGrazingReading(manualInput)) {
        for (const fieldGuid of grazingAffectedFieldGuids(queryClient, {
          fieldId,
          animalGroupGuid: animalGroupGuidFromReading(manualInput),
        })) {
          void invalidateUnlessReplaying(queryClient, {
            queryKey: queryKeys.fields.detail(fieldGuid),
          });
        }
      }
    },
  };
}

function deleteReadingMutationOptions(
  queryClient: QueryClient
): UseMutationOptions<
  {
    readingId: string;
    fieldId: string;
    isGrazing?: boolean;
    animalGroupGuid?: string;
  },
  unknown,
  {
    readingId: string;
    fieldId: string;
    isGrazing?: boolean;
    animalGroupGuid?: string;
  },
  ReadingMutationContext
> {
  return {
    networkMode: "online",
    scope: APP_MUTATION_SCOPE,
    retry: shouldRetryNetworkMutation,
    retryDelay: createNetworkMutationRetryDelay(),
    mutationFn: async (params) => {
      const res = await manualInputMutationApi.deleteOne(params.readingId);
      if (!res.ok) {
        const error = await res
          .json()
          .catch(() => ({ message: res.statusText }));
        throw error;
      }
      return params;
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.events.readings.byField(params.fieldId),
      });
      const snapshot = [
        ...snapshotReadings(queryClient, params.fieldId),
        ...snapshotMoveEvents(queryClient, params.animalGroupGuid),
      ];
      queryClient.setQueryData<ManualDMInputResponse[]>(
        queryKeys.events.readings.byField(params.fieldId),
        (old) =>
          (old || []).filter((reading) => reading.guid !== params.readingId)
      );
      snapshot.push(
        ...removeMoveEventFromCache(queryClient, {
          readingId: params.readingId,
          fieldId: params.fieldId,
          animalGroupGuid: params.animalGroupGuid,
        })
      );
      if (params.isGrazing) {
        resetTilesForGrazingChange(queryClient, {
          fieldId: params.fieldId,
          animalGroupGuid: params.animalGroupGuid,
        });
      }
      return { snapshot };
    },
    onError: (_error, params, context) => {
      if (context?.snapshot) {
        restoreSnapshot(queryClient, context.snapshot);
      }
      if (params.isGrazing) {
        resetTilesForGrazingChange(queryClient, {
          fieldId: params.fieldId,
          animalGroupGuid: params.animalGroupGuid,
        });
      }
    },
    onSettled: (_data, _error, params) => {
      void invalidateUnlessReplaying(queryClient, {
        queryKey: queryKeys.events.readings.byField(params.fieldId),
      });
      if (params.isGrazing) {
        for (const fieldGuid of grazingAffectedFieldGuids(queryClient, {
          fieldId: params.fieldId,
          animalGroupGuid: params.animalGroupGuid,
        })) {
          void invalidateUnlessReplaying(queryClient, {
            queryKey: queryKeys.fields.detail(fieldGuid),
          });
        }
      }
    },
  };
}

export function registerReadingMutationDefaults(
  queryClient: QueryClient
): void {
  queryClient.setMutationDefaults(
    ["createReading"],
    createReadingMutationOptions(queryClient)
  );
  queryClient.setMutationDefaults(
    ["updateReading"],
    updateReadingMutationOptions(queryClient)
  );
  queryClient.setMutationDefaults(
    ["deleteReading"],
    deleteReadingMutationOptions(queryClient)
  );
}

/**
 * Create a reading.
 */
export function useCreateReading() {
  const queryClient = useQueryClient();

  return useQueuedReadingMutation(
    {
      ...createReadingMutationOptions(queryClient),
      mutationKey: ["createReading"],
    },
    (manualInput) => isGrazingReading(manualInput)
  );
}

/**
 * Update a reading.
 */
export function useUpdateReading() {
  const queryClient = useQueryClient();

  return useQueuedReadingMutation(
    {
      ...updateReadingMutationOptions(queryClient),
      mutationKey: ["updateReading"],
    },
    (manualInput) => isGrazingReading(manualInput)
  );
}

/**
 * Delete a reading.
 */
export function useDeleteReading() {
  const queryClient = useQueryClient();

  return useQueuedReadingMutation(
    {
      ...deleteReadingMutationOptions(queryClient),
      mutationKey: ["deleteReading"],
    },
    (params) => !!params.isGrazing
  );
}

/**
 * Create multiple readings in bulk.
 */
export function useCreateReadingsBulk() {
  const queryClient = useQueryClient();

  return useGuardedMutation({
    mutationFn: async (manualInputs: Array<ManualDMInputResponse>) => {
      const preparedInputs = manualInputs.map(prepareReadingForApi);

      const res = await fetch(
        `${import.meta.env.VITE_API_ENDPOINT}/v4/manualDryMatterInput/bulk`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ manualDryMatterInputs: preparedInputs }),
        }
      );
      if (res.ok === false) {
        const error = await res
          .json()
          .catch(() => ({ message: res.statusText }));
        throw error;
      }

      return res.json() as Promise<unknown>;
    },
    onSuccess: async (data) => {
      // Invalidate all readings queries to refetch
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.readings.all,
      });
      resetTilesForReadingMutationResult(
        queryClient,
        data as ReadingMutationResult
      );
    },
  });
}
